import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentIcon,
  PaperAirplaneIcon,
  QrCodeIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import {
  Modal,
  Table,
  Button,
  Input,
  ButtonIcon,
} from '@rabbit/elements/shared-components';
import { toast } from 'react-toastify';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { DTVendable, FBD_HoldingProxy } from '@rabbit/data/types';
import { format } from 'date-fns';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';

interface SerialNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  vendable: DTVendable | null;
  config: any;
}

export const SerialNumberModal: React.FC<SerialNumberModalProps> = ({
  isOpen,
  onClose,
  vendable,
  config,
}) => {
  const { t } = useTranslation();
  const [serialNumbersData, setSerialNumbersData] = useState<
    {
      serialNumber: string;
      status: React.ReactNode;
      statusText: string; // Added string value for filtering
      actions: React.ReactNode;
      registerDate?: React.ReactNode;
      consumerName?: React.ReactNode;
      consumerEmail?: React.ReactNode;
    }[]
  >([]);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
  const [selectedSerialNumber, setSelectedSerialNumber] = useState<string>('');

  // Common styles for print windows
  const getPrintStyles = () => `
    body {
      font-family: 'Nunito', sans-serif;
      padding: 20px;
      text-align: center;
    }
    .container {
      max-width: 500px;
      margin: 0 auto;
    }
    .section {
      margin-bottom: 30px;
    }
    h1 {
      font-size: 24px;
      margin-bottom: 20px;
      color: #3B7D70;
    }
    h2 {
      font-size: 18px;
      margin-bottom: 10px;
    }
    .barcode-container {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 15px;
      margin-bottom: 20px;
      display: flex;
      justify-content: center;
    }
    .url {
      font-size: 12px;
      word-break: break-all;
      margin-top: 10px;
      color: #6b7280;
    }
    @media print {
      body {
        padding: 0;
      }
      .no-print {
        display: none;
      }
    }
  `;

  // Function to handle printing both the barcode and QR code
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print the barcode');
      return;
    }

    const barcodeUrl = generateBarcodeUrl();

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Barcode - ${selectedSerialNumber}</title>
          <style>${getPrintStyles()}</style>
        </head>
        <body>
          <div class="container">
            <h1>Barcode - ${selectedSerialNumber}</h1>

            <div class="section">
              <h2>QR Code</h2>
              <div class="barcode-container">
                <svg xmlns="http://www.w3.org/2000/svg" width="180" height="180" viewBox="0 0 180 180">
                  ${
                    document.querySelector('.flex.h-48.w-48 svg')?.outerHTML ||
                    ''
                  }
                </svg>
              </div>
              <div class="url">${barcodeUrl}</div>
            </div>

            <div class="section">
              <h2>Barcode</h2>
              <div class="barcode-container">
                ${
                  document.querySelector(
                    '.flex.items-center.justify-center.overflow-hidden svg'
                  )?.outerHTML || ''
                }
              </div>
              <div class="url">Serial Number: ${selectedSerialNumber}</div>
            </div>

            <div class="no-print">
              <button onclick="window.print(); window.close();" style="background-color: #3B7D70; color: white; border: none; border-radius: 4px; padding: 8px 16px; cursor: pointer; font-family: 'Nunito', sans-serif;">
                Print
              </button>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
  };

  // Function to handle printing only the QR code
  const handlePrintQRCode = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print QR code');
      return;
    }

    const barcodeUrl = generateBarcodeUrl();

    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Code - ${selectedSerialNumber}</title>
          <style>${getPrintStyles()}</style>
        </head>
        <body>
          <div class="container">
            <h1>QR Code - ${selectedSerialNumber}</h1>

            <div class="section">
              <div class="barcode-container">
                <svg xmlns="http://www.w3.org/2000/svg" width="180" height="180" viewBox="0 0 180 180">
                  ${
                    document.querySelector('.flex.h-48.w-48 svg')?.outerHTML ||
                    ''
                  }
                </svg>
              </div>
              <div class="url">${barcodeUrl}</div>
            </div>

            <div class="no-print">
              <button onclick="window.print(); window.close();" style="background-color: #3B7D70; color: white; border: none; border-radius: 4px; padding: 8px 16px; cursor: pointer; font-family: 'Nunito', sans-serif;">
                Print
              </button>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
  };

  // Function to handle printing only the barcode
  const handlePrintBarcode = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print barcode');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Barcode - ${selectedSerialNumber}</title>
          <style>${getPrintStyles()}</style>
        </head>
        <body>
          <div class="container">
            <h1>Barcode - ${selectedSerialNumber}</h1>

            <div class="section">
              <div class="barcode-container">
                ${
                  document.querySelector(
                    '.flex.items-center.justify-center.overflow-hidden svg'
                  )?.outerHTML || ''
                }
              </div>
              <div class="url">Serial Number: ${selectedSerialNumber}</div>
            </div>

            <div class="no-print">
              <button onclick="window.print(); window.close();" style="background-color: #3B7D70; color: white; border: none; border-radius: 4px; padding: 8px 16px; cursor: pointer; font-family: 'Nunito', sans-serif;">
                Print
              </button>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
  };

  // Function to compare alphanumeric strings
  const compareAlphanumeric = (a: string, b: string) => {
    // Natural sort comparison for alphanumeric strings
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  // Function to expand a range of serial numbers
  const expandSerialNumberRange = (start: string, end: string): string[] => {
    // If the serial numbers are purely numeric, we can generate the range easily
    const startNum = parseInt(start);
    const endNum = parseInt(end);

    if (
      !isNaN(startNum) &&
      !isNaN(endNum) &&
      start === startNum.toString() &&
      end === endNum.toString()
    ) {
      // Handle numeric serial numbers
      const result: string[] = [];
      for (let i = startNum; i <= endNum; i++) {
        result.push(i.toString());
      }
      return result;
    } else {
      // For alphanumeric serial numbers, we need a more complex approach
      // Extract the prefix and numeric part if possible
      const prefixMatch = start.match(/^([^\d]*)(\d+)$/);

      if (prefixMatch && end.startsWith(prefixMatch[1])) {
        const prefix = prefixMatch[1];
        const startDigits = parseInt(prefixMatch[2]);
        const endDigits = parseInt(end.substring(prefix.length));

        if (!isNaN(startDigits) && !isNaN(endDigits)) {
          const result: string[] = [];
          for (let i = startDigits; i <= endDigits; i++) {
            result.push(prefix + i.toString());
          }
          return result;
        }
      }

      // If we can't parse the range in a meaningful way, just return the start and end
      return [start, end];
    }
  };

  // Function to handle opening the serial number modal
  const loadSerialNumberData = async (vendable: DTVendable) => {
    if (!vendable) return;

    // Get serial numbers from the vendable
    const serialNumbers = vendable.serialNumbers || [];
    const serialNumberRanges = vendable.serialNumberRanges || [];

    // Expand ranges into individual serial numbers
    const expandedRangeSerialNumbers: string[] = [];
    serialNumberRanges.forEach((range) => {
      const start = range.start;
      const end = range.end;

      // Only expand the range if start is less than or equal to end
      if (compareAlphanumeric(start, end) <= 0) {
        const expandedRange = expandSerialNumberRange(start, end);
        expandedRangeSerialNumbers.push(...expandedRange);
      } else {
        // If the range is invalid, just add the start and end
        expandedRangeSerialNumbers.push(start, end);
      }
    });

    // Combine all serial numbers
    const allSerialNumbers = [...serialNumbers, ...expandedRangeSerialNumbers];

    // Firestore "in" operator supports only up to 30 values, so we need to batch the queries
    const BATCH_SIZE = 30;
    const batches = [];

    // Split allSerialNumbers into batches of BATCH_SIZE
    for (let i = 0; i < allSerialNumbers.length; i += BATCH_SIZE) {
      batches.push(allSerialNumbers.slice(i, i + BATCH_SIZE));
    }

    // Make multiple queries, one for each batch
    const batchQueries = batches.map((batch) =>
      FBD_HoldingProxy.query()
        .where('vendable', '==', vendable.docid)
        .where('serial', 'in', batch)
        .getContents()
    );

    // Wait for all queries to complete
    const batchResults = await Promise.all(batchQueries);

    // Combine the results from all batches
    const holdingProxies = batchResults.flat();

    // Create a set of used serial numbers for faster lookup
    const usedSerialNumbers = new Set(
      holdingProxies.map((proxy) => proxy.serial)
    );

    // Create the status data for each serial number
    const serialNumbersWithStatus = allSerialNumbers.map((serialNumber) => {
      // If the serial number is found in the holding proxy, it's used
      const status = usedSerialNumbers.has(serialNumber) ? 'Used' : 'Active';

      // Create the base object with common properties
      const result: any = {
        serialNumber,
        statusText: status, // Add string value for filtering
        status: (
          <span
            className={`font-nunito inline-block rounded-full px-3 py-1 text-sm font-semibold ${
              status === 'Active'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {status}
          </span>
        ),
        actions: (
          <div className="flex justify-center gap-2">
            <ButtonIcon
              kind="bgGreen"
              Icon={PaperAirplaneIcon}
              onClick={() => handleOpenEmailModal(serialNumber)}
              label={''}
            />
            <ButtonIcon
              kind="bgGreen"
              Icon={QrCodeIcon}
              onClick={() => handleOpenBarcodeModal(serialNumber)}
              label={''}
            />
          </div>
        ),
      };

      // If the serial number is used, add additional information
      if (status === 'Used') {
        // Find the holding proxy for this serial number
        const holdingProxy = holdingProxies.find(
          (proxy) => proxy.serial === serialNumber
        );

        if (holdingProxy) {
          // Format the registration date
          if (holdingProxy.register_time) {
            const registerDate = new Date(holdingProxy.register_time);
            result.registerDate = format(registerDate, 'dd/MM/yyyy');
          }

          // Add consumer name if available
          if (holdingProxy.consumer_name) {
            result.consumerName = holdingProxy.consumer_name;
          }

          // Add consumer email if available
          if (holdingProxy.consumer_email) {
            result.consumerEmail = holdingProxy.consumer_email;
          }
        }
      }

      return result;
    });

    setSerialNumbersData(serialNumbersWithStatus);
  };

  // Function to handle opening the email modal
  const handleOpenEmailModal = (serialNumber: string) => {
    setSelectedSerialNumber(serialNumber);
    setIsEmailModalOpen(true);
  };

  // Function to handle sending the email
  const handleSendEmail = (email: string) => {
    // In a real implementation, you would send an email with the serial number information
    toast.success(
      `Email sent to ${email} with serial number ${selectedSerialNumber}`
    );
    setIsEmailModalOpen(false);
  };

  // Function to handle opening the barcode modal
  const handleOpenBarcodeModal = (serialNumber: string) => {
    setSelectedSerialNumber(serialNumber);
    setIsBarcodeModalOpen(true);
  };

  // Function to generate the barcode URL
  const generateBarcodeUrl = () => {
    if (!vendable || !selectedSerialNumber) return '';

    // Get the host URL from the window location
    const host = window.location.origin;

    // Get the vendable mpn (Model Part Number)
    const sku = vendable.mpn || '';

    // Construct the URL
    return `${host}/register-product?sku=${sku}&sn=${selectedSerialNumber}`;
  };

  // Column definitions for the serial number table
  const serialNumberColumns = useMemo(
    () => [
      {
        header: t('general.serialNumber'),
        accessorKey: 'serialNumber',
        size: 150,
      },
      {
        header: t('general.serialNumberStatus'),
        id: 'status', // Use id instead of accessorKey
        size: 100,
        filterVariant: 'select',
        filterSelectOptions: ['Active', 'Used'],
        accessorFn: (row: any) => row.statusText, // Use statusText for filtering
        Cell: ({ row }: { row: any }) => row.original.status, // Display the status JSX element
      },
      {
        header: t('general.registrationDate'),
        accessorKey: 'registerDate',
        size: 150,
      },
      {
        header: t('general.consumerName'),
        accessorKey: 'consumerName',
        size: 200,
      },
      {
        header: t('general.email'),
        accessorKey: 'consumerEmail',
        size: 200,
      },
      {
        header: t('general.actions'),
        accessorKey: 'actions',
        size: 100,
      },
    ],
    [t]
  );

  // Load serial number data when the modal is opened
  useEffect(() => {
    if (isOpen && vendable) {
      loadSerialNumberData(vendable);
    }
  }, [isOpen, vendable]);

  return (
    <>
      {isOpen && (
        <Modal
          kind="generic"
          settings={{
            title: t('general.serialNumbers'),
            handleClose: onClose,
          }}
          className="w-full max-w-6xl rounded-md border shadow-lg"
        >
          <div className="bg-white p-6">
            <h2 className="font-nunito text-primary-900 mb-6 border-b pb-3 text-2xl font-bold">
              {vendable?.full} - {t('general.serialNumbers')}
            </h2>

            {serialNumbersData.length === 0 ? (
              <p className="font-nunito">
                {t('message.noDataAvailableToDisplay')}
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow">
                <Table
                  name="serial-numbers-table"
                  columns={serialNumberColumns}
                  data={serialNumbersData}
                  enableColumnFilters={Boolean(
                    config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                  )}
                  enableTopToolbar={
                    !config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                  }
                  advancedTable={Boolean(
                    config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                  )}
                  enableRowSelection={Boolean(
                    config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                  )}
                  enableBottomToolbar={Boolean(
                    config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                  )}
                  enableToolbarInternalActions={Boolean(
                    config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                  )}
                  enableFacetedValues={Boolean(
                    config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
                  )}
                  columnFilterDisplayMode="popover"
                  muiSearchTextFieldProps={{
                    placeholder: t('general.searchByRegistrationID'),
                  }}
                />
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Email Modal */}
      {isEmailModalOpen && (
        <Modal
          kind="generic"
          settings={{
            title: t('general.sendLink'),
            handleClose: () => setIsEmailModalOpen(false),
          }}
          className="w-full max-w-md rounded-md border shadow-lg"
        >
          <div className="bg-white p-6">
            <h3 className="font-nunito text-primary-900 mb-6 border-b pb-3 text-xl font-bold">
              {t('general.sendLink')} - {selectedSerialNumber}
            </h3>

            <Formik
              initialValues={{ email: '' }}
              validationSchema={Yup.object({
                email: Yup.string()
                  .email(t('message.pleaseEnterAValidEmailAddress'))
                  .required(t('message.pleaseEnterAValidEmailAddress')),
              })}
              onSubmit={(values, { resetForm }) => {
                handleSendEmail(values.email);
                resetForm();
              }}
            >
              {({ errors, touched }) => (
                <Form>
                  <div className="mb-6">
                    <label
                      htmlFor="email"
                      className="font-nunito mb-2 block text-sm font-medium text-gray-700"
                    >
                      {t('general.email')}
                    </label>
                    <Field
                      as={Input}
                      type="email"
                      name="email"
                      id="email"
                      settings={{
                        placeholder: t('general.email'),
                        className:
                          'w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500',
                      }}
                    />
                    {errors.email && touched.email && (
                      <div className="font-nunito mt-2 text-sm text-red-600">
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      kind="red"
                      type="button"
                      onClick={() => setIsEmailModalOpen(false)}
                      className="font-nunito rounded-md px-4 py-2 hover:bg-gray-100"
                    >
                      {t('general.cancel')}
                    </Button>
                    <Button
                      kind="primary"
                      type="submit"
                      className="font-nunito flex items-center gap-2 rounded-md px-4 py-2"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                      {t('general.send')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Modal>
      )}

      {/* Barcode Modal */}
      {isBarcodeModalOpen && (
        <Modal
          kind="generic"
          settings={{
            title: t('general.barcode'),
            handleClose: () => setIsBarcodeModalOpen(false),
          }}
          className="w-full max-w-md rounded-md border shadow-lg"
        >
          <div className="bg-white p-6">
            <h3 className="font-nunito text-primary-900 mb-6 border-b pb-3 text-xl font-bold">
              {t('general.barcode')} - {selectedSerialNumber}
            </h3>

            <Formik
              initialValues={{ barcodeUrl: generateBarcodeUrl() }}
              validationSchema={Yup.object({
                barcodeUrl: Yup.string().required('URL is required'),
              })}
              onSubmit={(values) => {
                // Handle form submission if needed
                setIsBarcodeModalOpen(false);
              }}
            >
              {({ errors, touched }) => (
                <Form>
                  <div className="mb-6">
                    <p className="font-nunito mb-4 text-sm text-gray-700">
                      {t('general.barcodeDescription')}
                    </p>

                    <div className="mb-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-nunito text-sm font-medium text-gray-700">
                          QR Code
                        </h4>
                        <Button
                          kind="primary"
                          type="button"
                          onClick={handlePrintQRCode}
                          className="font-nunito flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-gray-100"
                        >
                          <PrinterIcon className="h-3 w-3" />
                          {t('general.print')}
                        </Button>
                      </div>
                      <div className="mt-2 flex items-center justify-center">
                        <div className="flex h-48 w-48 items-center justify-center rounded-md border border-gray-300 bg-white p-2">
                          <QRCodeSVG
                            value={generateBarcodeUrl()}
                            size={180}
                            level="H"
                            includeMargin={true}
                            bgColor="#FFFFFF"
                            fgColor="#000000"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-nunito text-sm font-medium text-gray-700">
                          Barcode
                        </h4>
                        <Button
                          kind="primary"
                          type="button"
                          onClick={handlePrintBarcode}
                          className="font-nunito flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-gray-100"
                        >
                          <PrinterIcon className="h-3 w-3" />
                          {t('general.print')}
                        </Button>
                      </div>
                      <div className="mt-2 flex items-center justify-center overflow-hidden rounded-md border border-gray-300 bg-white p-4">
                        <Barcode
                          value={selectedSerialNumber}
                          width={1.5}
                          height={50}
                          format="CODE128"
                          displayValue={true}
                          font="nunito"
                          fontSize={12}
                          textMargin={6}
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label
                        htmlFor="barcodeUrl"
                        className="font-nunito mb-2 block text-sm font-medium text-gray-700"
                      >
                        {t('general.barcodeUrl')}
                      </label>
                      <Field
                        as={Input}
                        type="text"
                        name="barcodeUrl"
                        id="barcodeUrl"
                        settings={{
                          disabled: true,
                          className:
                            'w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-primary-500 focus:ring-primary-500',
                        }}
                      />
                      {errors.barcodeUrl && touched.barcodeUrl && (
                        <div className="font-nunito mt-2 text-sm text-red-600">
                          {errors.barcodeUrl}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      kind="primary"
                      type="button"
                      onClick={handlePrint}
                      className="font-nunito flex items-center gap-2 rounded-md px-4 py-2 hover:bg-gray-100"
                    >
                      <PrinterIcon className="h-4 w-4" />
                      {t('general.print')} All
                    </Button>
                    <Button
                      kind="primary"
                      type="button"
                      onClick={() => setIsBarcodeModalOpen(false)}
                      className="font-nunito rounded-md px-4 py-2"
                    >
                      {t('general.close')}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SerialNumberModal;
